import _ from "lodash";
import React from "react";
import i18n from "src/i18n/config";
import { MdmCommand } from "src/ui/containers/dashboard/devices/types/commands";
import useFetchInterval from "src/ui/hooks/useFetchInterval";
import { MinimalTableCell } from "src/ui/shared/table/MinimalTableComponents";

import { fetchDeviceMdmCommands } from "@dashboard/devices/api/devices";
import { Device, DeviceState } from "@dashboard/devices/types";
import { hasUnfinishedCommands } from "@dashboard/shared/components/SidePanel/shared/CommandTab/CommandTab.utils";
import { Typography } from "@mui/material";
import { ColumnDef } from "@tanstack/react-table";

import { StatusCell } from "./StatusCell";

const wifiCommandNames = ["enableWifi", "addWifiNetwork"];
const wifiCommandsPageSize = 25;

// Independent from the Commands tab's shared, paginated/sorted DeviceCommandProvider state -
// that state is scoped to whatever page the user is browsing there, so it can't reliably answer
// "what's the latest enableWifi/addWifiNetwork command" for this device.
export function useWifiCommands(deviceId: number) {
    const [commands, setCommands] = React.useState<MdmCommand[]>([]);

    const refresh = React.useCallback(async () => {
        try {
            const response = await fetchDeviceMdmCommands({
                deviceId,
                pageIndex: 1,
                pageSize: wifiCommandsPageSize,
                sortCriteria: "created",
                sortDirection: "descending",
            });
            setCommands(response.list.filter((command) => wifiCommandNames.includes(command.payload.command)));
        } catch (_error) {
            setCommands([]);
        }
    }, [deviceId]);

    React.useEffect(() => {
        void refresh();
    }, [refresh]);

    useFetchInterval(refresh, { frequency: 10000, condition: hasUnfinishedCommands(commands) });

    return { commands, refresh };
}

export type TableRowData = {
    id: number;
    name: string;
    hidden: string;
    signal: number | string;
    status: "connected" | "disconnected" | "pending";
};
export const columns: ColumnDef<TableRowData, unknown>[] = [
    {
        id: "name",
        header: i18n.t("common:label.name"),
        accessorKey: "name",
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.row.original.name}</Typography>
            </MinimalTableCell>
        ),
    },
    {
        id: "hidden",
        header: i18n.t("sidepanels:devicePanel.networkTab.hidden"),
        accessorKey: "hidden",
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.row.original.hidden}</Typography>
            </MinimalTableCell>
        ),
    },
    {
        id: "signal",
        header: i18n.t("sidepanels:devicePanel.networkTab.signal"),
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.row.original.signal}</Typography>
            </MinimalTableCell>
        ),
    },
    {
        id: "status",
        header: i18n.t("sidepanels:devicePanel.networkTab.status"),
        cell: (info) => <StatusCell status={info.row.original.status} />,
    },
];

function isConnected(
    wifi: DeviceState["wifi"],
    network: {
        id: number;
        name: string;
        hidden: boolean;
    },
): boolean {
    return !!wifi?.connected && network.name.replace(/^"|"$/g, "") === wifi?.ssid?.replace(/^"|"$/g, "");
}

export function useGetTableData(wifi: DeviceState["wifi"], pendingCommand: MdmCommand) {
    return React.useMemo(() => {
        const saved = _.uniqBy(wifi?.saved ?? [], "name");
        const strength = wifi?.strength ?? 0;

        const result: TableRowData[] = [];
        let hasConnectedRow = false;

        saved.forEach((network) => {
            const connected = isConnected(wifi, network);
            if (connected) {
                hasConnectedRow = true;
            }
            result.push({
                id: network.id,
                name: network.name,
                hidden: network.hidden ? i18n.t("common:label.yes") : i18n.t("common:label.no"),
                signal: connected ? strength : "-",
                status: connected ? "connected" : "disconnected",
            });
        });

        if (pendingCommand) {
            result.push({
                id: -1,
                name: pendingCommand.payload.ssid as string,
                hidden: pendingCommand.payload.hidden ? i18n.t("common:label.yes") : i18n.t("common:label.no"),
                signal: "-",
                status: "pending",
            });
        }

        if (wifi?.connected && wifi?.ssid && wifi?.mac && wifi?.strength !== undefined && !hasConnectedRow) {
            // The currently connected network isn't among the saved ones (e.g. older device states have no
            // "saved" attribute at all), so surface it as a fallback row.
            result.push({
                id: -2,
                name: wifi.ssid,
                hidden: i18n.t("common:label.no"),
                signal: wifi.strength,
                status: "connected",
            });
        }

        return result;
    }, [wifi, pendingCommand]);
}

// Runs `onSettled` once when `isPending` transitions from true to false - i.e. right after a
// command resolves - passing the settled command so callers can refetch the device and pick up its
// real, post-command state and inspect the final status
function useRefetchOnSettled(isPending: boolean, lastCommand: MdmCommand | undefined, onSettled: (command?: MdmCommand) => void) {
    const prevIsPending = React.useRef(false);

    React.useEffect(() => {
        if (prevIsPending.current && !isPending) {
            onSettled(lastCommand);
        }
        prevIsPending.current = isPending;
    }, [isPending, lastCommand, onSettled]);
}

export function useLastChangeWifiCommand(
    commands: MdmCommand[],
    onSettled: (command?: MdmCommand) => void,
): { isPending: boolean } {
    const sortedCommands = _.orderBy(
        commands.filter((command) => command.payload.command === "enableWifi"),
        "created",
        "desc",
    );
    const lastCommand = sortedCommands[0];
    const isPending = Boolean(
        lastCommand &&
        lastCommand?.status !== "successful" &&
        lastCommand?.status !== "ignored" &&
        lastCommand?.status !== "failed",
    );
    useRefetchOnSettled(isPending, lastCommand, onSettled);
    return { isPending };
}

export function useLastAddedWifiCommand(
    commands: MdmCommand[],
    onSettled: (command?: MdmCommand) => void,
): { isPending: boolean; lastAddedCommand: MdmCommand } {
    const sortedCommands = _.orderBy(
        commands.filter((command) => command.payload.command === "addWifiNetwork"),
        "created",
        "desc",
    );
    const lastAddedCommand = sortedCommands[0];
    const isPending = Boolean(
        lastAddedCommand &&
        lastAddedCommand?.status !== "successful" &&
        lastAddedCommand?.status !== "ignored" &&
        lastAddedCommand?.status !== "failed",
    );
    useRefetchOnSettled(isPending, lastAddedCommand, onSettled);
    return { isPending, lastAddedCommand };
}

export function isWifiEnabled(device: Device): boolean {
    if (!device?.isOnline) {
        return false;
    }
    return !!device?.lastState?.wifi?.enabled;
}
